/**
 * Right-click context menu for frames.
 *
 * Wraps any frame component. On the canvas it provides the frame's
 * secondary actions: Copy, project move, contextual details, Hide, and
 * Delete. A surface that owns the right-click (`FrameRenderer.contextMenu`:
 * opened items, the wall's Pinned tiles) shows its own menu instead.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Icons } from "@/components/ui/icons";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { translate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { useFrameActions } from "@/components/shapes/shared/use-frame-actions";
import { useFrameEditController } from "@/components/shapes/shared/frame-edit-controller";
import { useFrameAttention } from "@/components/shapes/shared/use-frame-attention";
import { useFrameRenderer } from "@/components/shapes/shared/frame-renderer-context";
import { answerInInputBar } from "@/lib/answer-in-input-bar";
import { dismissFrameError } from "@/lib/dismiss-frame-error";
import { useCanvasStore } from "@/stores/canvas-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useFrame } from "@/stores/frames-selectors";
import { selectFrameWindow } from "@/lib/focus-frame-activation";
import { openTaskDetails } from "@/lib/right-panel-navigation";
import { useNotificationStore } from "@/stores/notification-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import {
  setFramePinned,
  useCanPinFrame,
  useFramePinned,
} from "@/lib/frame-pinning";
import { isTextEntryTarget } from "@/lib/editable-target";
import { backToWorkspace } from "@/lib/focus/focus-back";
import {
  enterExplicitFocus,
  enterFocusPresentationMode,
} from "@/lib/focus-presentation";
import { FrameContentMenuItems } from "./frame-content-menu-items";
import { FrameMoveToProjectDialog } from "./frame-move-to-project-dialog";
import {
  bridgeIframeContextMenus,
  contextMenuSelection,
} from "./iframe-context-menu-bridge";
import { OpenedFrameContextMenu } from "./opened-frame-context-menu";
import type { FrameData } from "@/lib/frame-data";
import { deleteFrames, moveFramesToProject } from "@/lib/frame-menu-actions";

interface FrameContextMenuProps {
  frameId: string;
  children: ReactNode;
  fallbackFrame?: FrameData;
  onFocus?: () => void;
  onDismiss?: () => void;
}

export function FrameContextMenu(props: FrameContextMenuProps) {
  // The renderer is fixed for a mounted frame, so the branch never swaps.
  return useFrameRenderer().contextMenu ? (
    <OpenedFrameContextMenu
      frameId={props.frameId}
      fallbackFrame={props.fallbackFrame}
    >
      {props.children}
    </OpenedFrameContextMenu>
  ) : (
    <CanvasFrameContextMenu {...props} />
  );
}

function CanvasFrameContextMenu({
  frameId,
  children,
  fallbackFrame,
  onFocus,
  onDismiss,
}: FrameContextMenuProps) {
  const { handleDelete: handleHide } = useFrameActions(frameId);
  const canPinFromMenu = useCanPinFrame(frameId);
  const isPinned = useFramePinned(frameId);
  const t = useT(framesSharedMessages);
  const tCommon = useT(commonMessages);
  const editController = useFrameEditController(frameId);
  const attention = useFrameAttention(frameId);
  const [attentionPending, setAttentionPending] = useState(false);

  const storedFrameData = useFrame(frameId);
  const frameData = storedFrameData ?? fallbackFrame ?? null;
  // Focus as the canvas presentation shows it: a floating window has its
  // own close and never offers "Back to workspace" here.
  const renderer = useFrameRenderer();
  const chromeFocused = renderer.useChromeState(frameId).focused;
  const focused =
    !onFocus && chromeFocused && renderer.closeFrame === undefined;
  const multiIds = useSelectionStore((s) => s.multiSelectedFrameIds);
  const openMultiSelectContextMenu = useCanvasStore(
    (s) => s.openMultiSelectContextMenu,
  );

  const isMultiSelected =
    !onFocus && !focused && multiIds.length > 1 && multiIds.includes(frameId);
  const showDetails = Boolean(frameData?.taskTicket);
  const canFocusFromMenu = Boolean(frameData);
  const frameMenuRootRef = useRef<HTMLDivElement>(null);
  const selectedTextRef = useRef("");
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  useEffect(() => {
    const root = frameMenuRootRef.current;
    if (root) return bridgeIframeContextMenus(root);
  }, [isMultiSelected]);

  const handleAttention = async () => {
    if (!attention) return;
    setAttentionPending(true);
    try {
      if (attention.kind === "question")
        await answerInInputBar(attention.awaitingInputId);
      else await dismissFrameError(frameId);
    } catch (error) {
      useNotificationStore.getState().addSystemToast({
        id: `frame-attention-${frameId}`,
        title: translate(framesSharedMessages, "updateFailed"),
        description:
          error instanceof Error
            ? error.message
            : translate(framesSharedMessages, "tryAgain"),
        level: "error",
      });
    } finally {
      setAttentionPending(false);
    }
  };

  const captureSelectedText = useCallback((event: React.MouseEvent) => {
    selectedTextRef.current = contextMenuSelection(
      frameMenuRootRef.current,
      event.target,
    );
  }, []);

  const handleShowDetails = useCallback(() => {
    if (frameData?.taskTicket) void openTaskDetails(frameData.taskTicket.id);
  }, [frameData?.taskTicket]);

  const handleFocus = useCallback(() => {
    if (onFocus) {
      onFocus();
      return;
    }
    const desktopMode = useDesktopMode.getState();
    if (desktopMode.mode === "denk") {
      enterExplicitFocus([frameId]);
      return;
    }
    enterFocusPresentationMode(frameId);
  }, [frameId, onFocus]);

  const handleBackToWorkspace = useCallback(() => {
    backToWorkspace(frameId);
  }, [frameId]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeletePermanently = useCallback(() => {
    void deleteFrames([frameId]).then((deleted) => {
      if (deleted) onDismiss?.();
    });
  }, [frameId, onDismiss]);

  const handleMoveFrame = useCallback(
    async (toProjectId: string) => {
      const moved = await moveFramesToProject([frameId], toProjectId);
      if (moved) onDismiss?.();
      return moved;
    },
    [frameId, onDismiss],
  );

  const handleMultiContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!isMultiSelected || isTextEntryTarget(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      openMultiSelectContextMenu(e.clientX, e.clientY);
    },
    [isMultiSelected, openMultiSelectContextMenu],
  );

  return (
    <>
      {isMultiSelected ? (
        <div
          ref={frameMenuRootRef}
          onContextMenu={handleMultiContextMenu}
          className="contents"
        >
          {children}
        </div>
      ) : (
        <ContextMenu>
          <ContextMenuTrigger>
            {/* The menu items are identical across frame types — select the
                target frame on open so the selection ring shows which frame
                Hide/Delete will act on (matters when frames overlap). */}
            <div
              ref={frameMenuRootRef}
              className="contents"
              onContextMenu={(event) => {
                if (isTextEntryTarget(event.target)) return;
                captureSelectedText(event);
                selectFrameWindow(frameId);
              }}
              onKeyDown={(event) => {
                if (isTextEntryTarget(event.target)) return;
                if (
                  event.key === "ContextMenu" ||
                  (event.shiftKey && event.key === "F10")
                ) {
                  selectedTextRef.current = "";
                  selectFrameWindow(frameId);
                }
              }}
              data-testid={`frame-context-menu-target-${frameId}`}
            >
              {children}
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            {frameData && (
              <FrameContentMenuItems
                frame={frameData}
                menuRootRef={frameMenuRootRef}
                selectedTextRef={selectedTextRef}
              />
            )}
            {canFocusFromMenu && (
              <ContextMenuItem
                icon={focused ? <Icons.ArrowLeft /> : <Icons.Scan />}
                onClick={focused ? handleBackToWorkspace : handleFocus}
                data-testid={focused ? "ctx-back-workspace" : "ctx-focus-frame"}
              >
                {focused ? t("backToWorkspace") : t("focusFrame")}
              </ContextMenuItem>
            )}
            {canPinFromMenu && (
              <ContextMenuItem
                icon={<Icons.Bookmark />}
                onClick={() => setFramePinned(frameId, !isPinned)}
                data-testid="frame-context-menu-pin"
              >
                {isPinned ? t("removeFrameBookmark") : t("bookmarkFrame")}
              </ContextMenuItem>
            )}
            {editController.available && (
              <ContextMenuItem
                icon={
                  editController.editing ? <Icons.Check /> : <Icons.Edit2 />
                }
                onClick={() =>
                  void (editController.editing
                    ? editController.saveEdits()
                    : editController.beginEditing())
                }
                disabled={editController.saving}
                data-testid="ctx-edit-frame"
              >
                {editController.editing ? t("saveFrameEdits") : t("editFrame")}
              </ContextMenuItem>
            )}
            {attention && (
              <ContextMenuItem
                icon={
                  attention.kind === "question" ? (
                    <Icons.MessageCircle />
                  ) : (
                    <Icons.X />
                  )
                }
                onClick={() => void handleAttention()}
                disabled={attentionPending}
                data-testid="ctx-attention"
              >
                {attention.kind === "question"
                  ? t("answerRequest")
                  : t("dismissError")}
              </ContextMenuItem>
            )}
            {showDetails && (
              <ContextMenuItem
                icon={<Icons.ListTodo />}
                onClick={handleShowDetails}
                data-testid="ctx-show-details"
              >
                {t("showTaskDetails")}
              </ContextMenuItem>
            )}
            <ContextMenuSeparator />
            <ContextMenuItem
              icon={<Icons.FolderKanban />}
              onClick={() => setProjectDialogOpen(true)}
              data-testid="ctx-move-project"
            >
              {t("moveToProjectMenu")}
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem
              icon={<Icons.EyeOff />}
              onClick={() => {
                handleHide();
                onDismiss?.();
              }}
              data-testid="ctx-hide"
            >
              {t("hide")}
            </ContextMenuItem>
            <ContextMenuItem
              destructive
              icon={<Icons.Trash2 />}
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="ctx-delete"
            >
              {tCommon("delete")}
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          title={t("deleteFrameTitle")}
          description={t("deleteFrameDescription")}
          confirmLabel={tCommon("delete")}
          variant="danger"
          icon={<Icons.Trash2 className="h-3.5 w-3.5 text-status-error" />}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            handleDeletePermanently();
          }}
          onCancel={() => setShowDeleteConfirm(false)}
          data-testid="frame-delete-confirm"
        />
      )}

      {projectDialogOpen && (
        <FrameMoveToProjectDialog
          frameIds={[frameId]}
          onMove={handleMoveFrame}
          onClose={() => setProjectDialogOpen(false)}
        />
      )}
    </>
  );
}
